import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

import {closeWebView} from '@zzng/common/scheme/web';

interface SlideOutOptions {
  onAnimationEnd: () => void;
}

const usePageTransition = <T extends HTMLElement>() => {
  const navigate = useNavigate();
  const transitionTarget = useRef<T>(null);
  const previousSibling = useRef<HTMLElement | null>(null);
  const currentBodyScrollTop = useRef(document.body.scrollTop);

  const blockTouch = () => {
    document.body.style.pointerEvents = 'none';
  };

  const enableTouch = () => {
    document.body.style.pointerEvents = 'auto';
  };

  const goBack = () => {
    navigate(-1);
  };

  const blockScrollPreviosSibling = () => {
    if (!previousSibling.current) return;
    previousSibling.current.style.overflow = 'hidden';
  };

  const enableScrollPreviosSibling = () => {
    if (!previousSibling.current) return;
    previousSibling.current.style.overflow = '';
    document.body.scrollTo(0, currentBodyScrollTop.current);
  };

  const slideOut = (options: SlideOutOptions = {onAnimationEnd: goBack}) => {
    if (!transitionTarget.current) {
      return;
    }

    if (history.length === 1) {
      closeWebView();
      return;
    }

    transitionTarget.current.onanimationstart = () => {
      blockTouch();
    };

    transitionTarget.current.onanimationend = () => {
      options.onAnimationEnd();
      enableTouch();
    };

    transitionTarget.current.classList.add('slide_out');

    enableScrollPreviosSibling();
  };

  const slideIn = () => {
    if (!transitionTarget.current) {
      return;
    }

    transitionTarget.current.onanimationstart = () => {
      blockTouch();
    };

    transitionTarget.current.onanimationend = () => {
      transitionTarget.current?.classList.remove('slide_in');
      transitionTarget.current?.classList.remove('slide_out');
      enableTouch();
      blockScrollPreviosSibling();
    };

    transitionTarget.current.classList.add('slide_in');
  };

  useEffect(() => {
    if (transitionTarget.current) {
      transitionTarget.current.classList.add('slide_container');
      previousSibling.current = transitionTarget.current.previousElementSibling as HTMLElement | null;
    }
    slideIn();

    return () => {
      if (previousSibling.current?.style.overflow === 'hidden') {
        enableScrollPreviosSibling();
      }
    };
  }, []);

  return {leave: slideOut, transitionTarget};
};
export default usePageTransition;
